/**
 * 파일명: aboutHistory.js
 * 작성자: 김준형
 * 작성일자: 26.08.10
 * 설명: About Us - 기업 연혁 / 솔루션 연혁
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import SectionReveal from "components/landing/sectionReveal";
import useAboutBg from "components/about/aboutAsset";
import style from "css/about.module.css";

const COMPANY_PERIODS = [
  {
    year: "2013-2015",
    label: "설립 및 협업 인프라 구축",
    labelSub: "(Foundation)",
    items: [
      { text: "(주)리브테크놀로지 회사 설립" },
      { text: "중소기업청 산학연협력 기술개발사업 선정 및 성공(완료)" },
      { text: "벤처기업 지정" },
      {
        text: "삼성전자 · 두산건설 · GS건설 · 인천광역시 화상회의 예약 및 관리 시스템 구축",
      },
      { text: "KT & Cisco 협업 기반 영상회의 운영 지원 시스템 개발" },
      { text: "삼성생명 영상회의 예약 시스템 구축" },
      { text: "Polycom Technical Partner 등록" },
      { text: "Cisco Collab Partner 등록" },
    ],
  },
  {
    year: "2016-2019",
    label: "엔터프라이즈 협업 환경 확장",
    labelSub: "(Expansion)",
    items: [
      { text: "연구개발 전담부서 설립" },
      { text: "삼성증권 화상회의 예약 시스템 구축" },
      {
        text: "현대/기아 자동차 · LH · 롯데월드타워 · 한국투자공사 · 한국도로공사 영상회의 예약 및 관리 시스템 구축",
      },
      { text: "SK하이닉스 CMS 연동 영상회의 예약 및 관리 시스템 구축" },
      {
        text: "CJ그룹 · 한국자산관리공사 · 우정사업본부 영상회의 예약 및 관리 시스템 구축",
      },
      {
        text: "청와대 · 식품의약품안전처 · 충남/강원 소방본부 · 한국철도시설공단 영상회의 서비스 관리 시스템 구축",
      },
    ],
  },
  {
    year: "2020-2021",
    label: "통합 게이트웨이 및 운영 플랫폼 확대",
    labelSub: "(Growth)",
    items: [
      { text: "LG Display WebEx 연동 영상회의 예약 및 관리 시스템 구축" },
      { text: "한국은행 · 한국타이어 영상회의 예약 포탈 구축" },
      { text: "롯데그룹 Cisco Teams 연동 게이트웨이 구축" },
      { text: "부산디지털대학교 WebEx 연동 게이트웨이 구축" },
      { text: "P4G 서울 녹색미래 정상회의 영상회의 서비스 구축" },
      {
        text: "신한카드 · SBI 저축은행 · 한국자산관리공사 CMS 연동 영상회의 예약 포탈 구축",
      },
    ],
  },
  {
    year: "2022",
    label: "글로벌 협업 파트너십 확대",
    labelSub: "(Partnership)",
    items: [
      { text: "기술부설연구소 설립" },
      { text: "외교부 화상회의 예약 관리 시스템 구축" },
      { text: "현대자동차 디자인센터 영상회의 예약 포탈 구축" },
      { text: "대상그룹 영상회의 협업 포탈 구축" },
      { text: "neat Advanced Partner 계약 체결" },
      { text: "Zoom Gold Partner 계약 체결" },
      { text: "Pexip Silver Partner 계약 체결" },
    ],
  },
  {
    year: "2023",
    label: "의료 · 엔터프라이즈 협업 사업 확장",
    labelSub: "(Scale-up)",
    items: [
      { text: "한림대학교의료원 비대면 진료 온케어 시스템 구축" },
      { text: "삼성바이오에피스 영상회의 예약 포탈 구축" },
      { text: "우아한형제들 neat 화상회의 장비 연단가 계약 체결" },
    ],
  },
  {
    year: "2024",
    label: "스마트 스페이스 구축 확대",
    labelSub: "(Smart Space)",
    items: [
      {
        text: "한양대학교 ERICA 캠퍼스 AI 기반 하이브리드 강의실 구축 및 협약 체결",
      },
      { text: "AI 서울 정상회의 화상회의 지원 시스템 구축" },
      { text: "민주주의 정상회의 화상회의 지원 시스템 구축" },
      { text: "쿠팡 Zoom 화상회의 모니터링 시스템 구축" },
    ],
  },
  {
    year: "2025-2026",
    label: "스마트워크 및 엔터프라이즈 고도화",
    labelSub: "(Innovation)",
    items: [
      {
        text: "디지털콘텐츠기업 성장지원센터 입주사 선정 및\n본사 안양 평촌 이전",
      },
      { text: "쿠팡 neat 화상회의 장비 연단가 계약 체결" },
      { text: "크래프톤 neat 화상회의 장비 연단가 계약 체결" },
      { text: "두산건설 모바일 서면교부 시스템 구축" },
      { text: "삼성서울병원 협진 플랫폼 구축 계약 체결" },
      { text: "쿠팡 신규 사옥 화상회의 환경 구축 계약 체결" },
      { text: "LX인터내셔널 경영진 대형 회의실 구축" },
      { text: "neat Premier Partner 승급(국내 유일)" },
    ],
  },
];

const SOLUTION_PERIODS = [
  {
    year: "2014-2015",
    label: "화상회의 예약 플랫폼 기술 확보",
    labelSub: "(Innovation)",
    items: [
      { text: "RMX 연동 화상회의 예약 시스템 RSP 자체 개발 및 출시" },
      { text: "WebEx 연동 화상회의 예약 시스템 WESS 자체 개발 및 출시" },
      { text: "Cisco TP Server 연동 VE Manager Plus 자체 개발 및 출시" },
    ],
  },
  {
    year: "2016-2021",
    label: "영상회의 운영·관리 플랫폼 고도화",
    labelSub: "(Platform Evolution)",
    items: [
      { text: "Cisco Meeting Server 관리 툴 CMS Manager 자체 개발 및 출시" },
      {
        text: "Cisco Meeting Server 연동 화상회의 예약 시스템 자체 개발 및 출시",
      },
      { text: "VE Manager for CMS 자체 개발 및 출시" },
      { text: "통합 게이트웨이 솔루션 Be Gate 자체 개발 및 출시" },
      { text: "영상회의 예약 포탈 VE Manager 및 RSP HTML5 버전 고도화" },
    ],
  },
  {
    year: "2022",
    label: "글로벌 협업 플랫폼 연동 확대",
    labelSub: "(Integration)",
    items: [
      { text: "Pexip Manager 자체 개발 및 출시" },
      { text: "Private Connector Manager 자체 개발 및 출시" },
      { text: "VE Manager for Zoom 출시" },
    ],
  },
  {
    year: "2023",
    label: "원격 의료 협진 플랫폼 출시",
    labelSub: "",
    items: [
      { text: "원격진료 및 원격협진 시스템 MediLink 개발 및 출시" },
      { text: "MediLink 기반 한림대학교의료원 비대면 진료 시스템 구축" },
    ],
  },
  {
    year: "2024",
    label: "스마트 공간 운영 플랫폼 확장",
    labelSub: "(Smart Space)",
    items: [
      { text: "Device One 출시" },
      { text: "Device One과 연계된 AI 기반 하이브리드 강의실 운영 환경 구축" },
    ],
  },
  {
    year: "2025-2026",
    label: "스마트워크 플랫폼 고도화",
    labelSub: "(Workspace SaaS)",
    items: [
      { text: "스마트 워크플레이스 솔루션 all Place 구축형 출시" },
      {
        text: "회의실 · 화상회의 · 자율좌석 예약 기반 스마트워크 플랫폼 SaaS 확장",
      },
      { text: "스마트 워크플레이스 솔루션 all Place 구독형 출시" },
    ],
  },
];

const HISTORY_GROUPS = [
  {
    key: "company",
    anchor: "company-history",
    label: "기업 연혁",
    title: "신뢰로 증명된 기업 성장 기록",
    periods: COMPANY_PERIODS,
  },
  {
    key: "solution",
    anchor: "solution-history",
    label: "솔루션 연혁",
    title: "독자적 기술로 완성해온 솔루션 혁신 과정",
    periods: SOLUTION_PERIODS,
  },
];

function ArrowIcon() {
  return (
    <svg
      className={style.ab_hist_nav_icon}
      viewBox="0 0 26 26"
      aria-hidden="true"
    >
      <path
        d="M4.33 13h17.34M13 4.33 21.67 13 13 21.67"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.73"
      />
    </svg>
  );
}

function HistoryGroup({ group }) {
  const trackRef = useRef(null);
  const [edge, setEdge] = useState({ start: true, end: true });
  const bg = useAboutBg();

  const syncState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;

    const max = el.scrollWidth - el.clientWidth;
    setEdge({ start: el.scrollLeft <= 1, end: el.scrollLeft >= max - 1 });
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;

    syncState();
    el.addEventListener("scroll", syncState);
    window.addEventListener("resize", syncState);

    return () => {
      el.removeEventListener("scroll", syncState);
      window.removeEventListener("resize", syncState);
    };
  }, [syncState]);

  const scrollByPage = (direction) => {
    const el = trackRef.current;
    if (!el) return;

    const cols = el.children;
    const pitch =
      cols.length > 1
        ? cols[1].getBoundingClientRect().left -
          cols[0].getBoundingClientRect().left
        : el.clientWidth;

    const step = Math.max(1, Math.floor(el.clientWidth / pitch) - 1);

    el.scrollBy({ left: pitch * step * direction, behavior: "smooth" });
  };

  return (
    <div id={group.anchor}>
      <div className={style.ab_hist_banner}>
        <span
          className={`${style.ab_hist_banner_img} ${style.ab_hist_banner_on}`}
          style={bg(`history_${group.key}`)}
        />

        <div className={style.ab_hist_banner_inner}>
          <span className={style.ab_hist_banner_label}>{group.label}</span>
          <strong className={style.ab_hist_banner_title}>{group.title}</strong>
        </div>

        <div className={style.ab_hist_nav}>
          <button
            type="button"
            className={`${style.ab_hist_nav_btn} ${style.ab_hist_nav_prev}`}
            onClick={() => scrollByPage(-1)}
            disabled={edge.start}
            aria-label={`이전 ${group.label}`}
          >
            <ArrowIcon />
          </button>
          <button
            type="button"
            className={style.ab_hist_nav_btn}
            onClick={() => scrollByPage(1)}
            disabled={edge.end}
            aria-label={`다음 ${group.label}`}
          >
            <ArrowIcon />
          </button>
        </div>
      </div>

      <div className={style.ab_hist_body}>
        <span className={style.ab_hist_rule} />

        <div
          className={style.ab_hist_track}
          ref={trackRef}
        >
          {group.periods.map((period) => (
            <div
              className={style.ab_hist_col}
              key={period.year}
            >
              <div className={style.ab_hist_col_head}>
                <div className={style.ab_hist_year}>{period.year}</div>
                <div className={style.ab_hist_label}>
                  {period.label}
                  {period.labelSub ? (
                    <>
                      <br />
                      {period.labelSub}
                    </>
                  ) : null}
                </div>
              </div>

              <ul className={style.ab_hist_items}>
                {period.items.map((item) => (
                  <li
                    className={style.ab_hist_item}
                    key={item.text}
                  >
                    <span className={style.ab_hist_dot} />
                    <span className={style.ab_hist_item_text}>{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AboutHistory() {
  return (
    <section className={style.ab_hist}>
      {HISTORY_GROUPS.map((group) => (
        <SectionReveal key={group.key}>
          <HistoryGroup group={group} />
        </SectionReveal>
      ))}
    </section>
  );
}

export default AboutHistory;
